// Formulaires de la fiche d'une demande d'urbanisme (lot L5-C2) : localisation, sollicitation et rendu d'un
// avis, inspection (ordre M23 existant ou nouvel ordre sur le lieu de la demande). Fenêtres et champs de la
// charte existante ; le serveur refait chaque contrôle (service consulté, dates, demande ouverte).
import type { DemandeUrbanisme, SensAvisUrbanisme } from '@cc/contrats';
import { SENS_AVIS_URBANISME } from '@cc/contrats';
import { Champ, Selection, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { useUtilisateurs } from '../../api/requetes';
import { useAction } from '../../commun/useAction';
import { Fenetre } from '../achats/Fenetre';
import { nomUtilisateur } from '../administration/PageUtilisateurs';
import { useServices } from '../budget/api';
import { useFormulaires, useOrdres } from '../inspections/api';
import { aujourdhuiIso } from '../proprete/logique';
import { INVALIDER_URBANISME, lierInspection, localiser, rendreAvis, solliciterAvis, usePlans } from './api';

type Props = { demande: DemandeUrbanisme; onFermer: () => void };

export function FormulaireLocalisation({ demande, onFermer }: Props) {
  const plans = usePlans();
  const [v, setV] = useState({ longitude: demande.position ? String(demande.position.longitude) : '', latitude: demande.position ? String(demande.position.latitude) : '', zone: '' });
  const zones = (plans.data?.elements ?? []).filter((p) => p.etat === 'en_vigueur').flatMap((p) => p.zones.map((z) => ({ valeur: z.id, libelle: `${z.code} · ${z.libelle} (${p.intitule})` })));
  const action = useAction({
    executer: () => localiser(demande.id, { position: { longitude: Number(v.longitude), latitude: Number(v.latitude) }, zone_id: v.zone || undefined }),
    succes: (d) => (d.zone ? `Demande localisée en zone ${d.zone.code}.` : 'Demande localisée hors zone.'),
    invalider: INVALIDER_URBANISME,
    apres: onFermer,
  });
  return (
    <Fenetre titre="Localiser la demande" description="La position est celle du dossier. Sans zone choisie, la zone est retenue par inclusion dans les zones des plans en vigueur." libelle="Enregistrer la position" enCours={action.enCours} onFermer={onFermer} onValider={() => action.lancer(undefined)} erreur={action.messageErreur}>
      <Champ libelle="Longitude" value={v.longitude} onChange={(e) => setV((x) => ({ ...x, longitude: e.target.value }))} required />
      <Champ libelle="Latitude" value={v.latitude} onChange={(e) => setV((x) => ({ ...x, latitude: e.target.value }))} required />
      <Selection libelle="Zone" options={zones} optionVide="Retenue par la position" value={v.zone} onChange={(e) => setV((x) => ({ ...x, zone: e.target.value }))} facultatif />
    </Fenetre>
  );
}

export function FormulaireSollicitation({ demande, onFermer }: Props) {
  const services = useServices();
  const deja = new Set(demande.avis_detail.map((a) => a.service.id));
  const requis = new Set(demande.type_detail.services_avis.map((s) => s.id));
  const options = (services.data?.elements ?? []).filter((s) => !deja.has(s.id)).map((s) => ({ valeur: s.id, libelle: `${s.nom}${requis.has(s.id) ? ' (avis requis)' : ''}` }));
  const [v, setV] = useState({ service: '', objet: '', echeance: '' });
  const action = useAction({
    executer: () => solliciterAvis(demande.id, { service_id: v.service, objet: v.objet || undefined, echeance: v.echeance || undefined }),
    succes: 'Avis sollicité.',
    invalider: INVALIDER_URBANISME,
    apres: onFermer,
  });
  return (
    <Fenetre titre="Solliciter un avis" description={`Échéance par défaut : ${demande.type_detail.delai_avis_jours} jours après la sollicitation (délai configuré pour ce type de demande).`} libelle="Solliciter" enCours={action.enCours} onFermer={onFermer} onValider={() => action.lancer(undefined)} erreur={action.messageErreur}>
      <Selection libelle="Service consulté" options={options} optionVide="Choisir un service" value={v.service} onChange={(e) => setV((x) => ({ ...x, service: e.target.value }))} required />
      <ZoneTexte libelle="Objet de la consultation" value={v.objet} onChange={(e) => setV((x) => ({ ...x, objet: e.target.value }))} facultatif />
      <Champ libelle="Échéance" type="date" value={v.echeance} onChange={(e) => setV((x) => ({ ...x, echeance: e.target.value }))} facultatif />
    </Fenetre>
  );
}

export function FormulaireRenduAvis({ demande, avisId, onFermer }: Props & { avisId: string }) {
  const avis = demande.avis_detail.find((a) => a.id === avisId);
  const [v, setV] = useState({ sens: 'favorable' as SensAvisUrbanisme, observations: '', date: aujourdhuiIso() });
  const action = useAction({
    executer: () => rendreAvis(demande.id, avisId, { sens: v.sens, observations: v.observations, date_avis: v.date }),
    succes: 'Avis rendu ; il est désormais figé.',
    invalider: INVALIDER_URBANISME,
    apres: onFermer,
  });
  return (
    <Fenetre titre={`Rendre l'avis : ${avis?.service.nom ?? ''}`} description="Seul le service consulté rend cet avis (responsable ou agent en poste). Un avis rendu ne se modifie plus." libelle="Rendre l'avis" enCours={action.enCours} onFermer={onFermer} onValider={() => action.lancer(undefined)} erreur={action.messageErreur}>
      <Selection libelle="Sens de l'avis" options={Object.entries(SENS_AVIS_URBANISME).map(([valeur, libelle]) => ({ valeur, libelle }))} value={v.sens} onChange={(e) => setV((x) => ({ ...x, sens: e.target.value as SensAvisUrbanisme }))} />
      <ZoneTexte libelle="Observations" value={v.observations} onChange={(e) => setV((x) => ({ ...x, observations: e.target.value }))} required />
      <Champ libelle="Date de l'avis" type="date" value={v.date} onChange={(e) => setV((x) => ({ ...x, date: e.target.value }))} required />
    </Fenetre>
  );
}

export function FormulaireInspection({ demande, onFermer }: Props) {
  const ordres = useOrdres({});
  const formulaires = useFormulaires({ actif: 'oui' });
  const utilisateurs = useUtilisateurs();
  const [v, setV] = useState({ ordre: '', controleur: '', formulaire: '', debut: aujourdhuiIso(), fin: aujourdhuiIso(), consignes: '', motif: '' });
  const lies = new Set(demande.inspections_detail.map((i) => i.ordre.id));
  const action = useAction({
    executer: () =>
      lierInspection(demande.id, v.ordre
        ? { ordre_id: v.ordre, motif: v.motif }
        : { controleur_appartenance_id: v.controleur, formulaire_id: v.formulaire, periode_debut: v.debut, periode_fin: v.fin, consignes: v.consignes || undefined, motif: v.motif }),
    succes: 'Inspection liée à la demande.',
    invalider: [...INVALIDER_URBANISME, ['inspections']],
    apres: onFermer,
  });
  return (
    <Fenetre titre="Inspection de la demande" description="Lier un ordre de mission existant, ou émettre un nouvel ordre sur le lieu de la demande (contrôles M23)." libelle="Lier l'inspection" enCours={action.enCours} onFermer={onFermer} onValider={() => action.lancer(undefined)} erreur={action.messageErreur}>
      <ZoneTexte libelle="Motif" value={v.motif} onChange={(e) => setV((x) => ({ ...x, motif: e.target.value }))} required />
      <Selection libelle="Ordre existant" options={(ordres.data ?? []).filter((o) => !lies.has(o.id)).map((o) => ({ valeur: o.id, libelle: `${o.numero} · ${o.objet_libelle ?? ''}` }))} optionVide="Émettre un nouvel ordre" value={v.ordre} onChange={(e) => setV((x) => ({ ...x, ordre: e.target.value }))} facultatif />
      {v.ordre ? null : (
        <>
          <Selection libelle="Contrôleur" options={(utilisateurs.data ?? []).filter((x) => x.statut_appartenance === 'active').map((x) => ({ valeur: x.appartenance_id, libelle: nomUtilisateur(x) }))} optionVide="Choisir un agent" value={v.controleur} onChange={(e) => setV((x) => ({ ...x, controleur: e.target.value }))} />
          <Selection libelle="Formulaire de constat" options={(formulaires.data ?? []).map((f) => ({ valeur: f.id, libelle: `${f.code} · ${f.nom}` }))} optionVide="Choisir un formulaire" value={v.formulaire} onChange={(e) => setV((x) => ({ ...x, formulaire: e.target.value }))} />
          <Champ libelle="Début" type="date" value={v.debut} onChange={(e) => setV((x) => ({ ...x, debut: e.target.value }))} />
          <Champ libelle="Fin" type="date" value={v.fin} onChange={(e) => setV((x) => ({ ...x, fin: e.target.value }))} />
          <ZoneTexte libelle="Consignes" value={v.consignes} onChange={(e) => setV((x) => ({ ...x, consignes: e.target.value }))} facultatif />
        </>
      )}
    </Fenetre>
  );
}
